import { useMutation } from '@apollo/client';
import React from 'react';
import AccountContentContainer from './AccountContentContainer';
import * as styles from './OAuthAuthorizeView.module.scss';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Buttons from '../components/Buttons';
import SVGIcon from '../components/SVGIcon';
import { gql } from '../graphql/generated/gql';
import apiIcon from '../icons/non-prefilled/16x16/api.svg';
import arrowBackForthIcon from '../icons/non-prefilled/16x16/arrowBackForth.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import TeamIcon, { TeamIconInner } from '../organization/TeamIcon';
import PageMeta from '../utils/PageMeta';
import Link from '../utils/router/Link';

const AUTHORIZE_MUTATION = gql(/** @complexity 2 */ `
  mutation oauthAuthorize($clientId: String!, $query: String!) {
    redirectTo: oauthAuthorize(clientId: $clientId, query: $query)
  }
`);

const {
  clientId = '',
  clientName = 'Unknown Client',
  clientUri,
  logoUri,
} = window.__slab.OAUTH_CLIENT || {};

export default ({ isMcp = false }: { isMcp?: boolean }) => {
  const { organization, user } = useAppSelector((state) => state.session);

  const [authorizeMcp] = useMutation(AUTHORIZE_MUTATION);
  const onAuthorize = () => {
    authorizeMcp({
      variables: { clientId, query: window.location.search },
    }).then(({ data: response }) => {
      if (response?.redirectTo) {
        window.location.href = response.redirectTo;
      }
    });
  };

  return (
    <AccountContentContainer
      title={clientName}
      subtitle="wants access to your Slab account"
    >
      <PageMeta title={`Authorize ${clientName} - Slab`} />
      <div className={styles.logoContainer}>
        <TeamIconInner name={clientName} logo={logoUri} size="large" />
        <SVGIcon>{arrowBackForthIcon}</SVGIcon>
        <TeamIcon size="large" />
      </div>
      <div className={styles.description}>
        {clientUri ? (
          <Link className={styles.clientName} to={clientUri} newTab>
            {clientName}
          </Link>
        ) : (
          <span className={styles.clientName}>{clientName}</span>
        )}{' '}
        {isMcp ? (
          <>
            will be able to on your behalf:
            <div className={styles.accessContainer}>
              <div className={styles.accessItem}>
                <SVGIcon inline>{searchIcon}</SVGIcon> Search for posts
              </div>
              <div className={styles.accessItem}>
                <SVGIcon inline>{postIcon}</SVGIcon> Retrieve full post contents
              </div>
              <div className={styles.accessItem}>
                <SVGIcon inline>{apiIcon}</SVGIcon> Run other read-only tools
              </div>
            </div>
          </>
        ) : (
          <>
            will be able to:
            <div className={styles.accessContainer}>
              <div className={styles.accessItem}>
                <SVGIcon inline>{apiIcon}</SVGIcon> Retrieve your API token
              </div>
            </div>
          </>
        )}
      </div>
      <Buttons>
        <Button
          block
          variant="primary"
          onClick={onAuthorize}
          disabled={!clientId}
        >
          Authorize
        </Button>
        <Button block variant="secondary" to="/">
          Cancel
        </Button>
      </Buttons>
      <div className={styles.loggedIn}>
        Logged in as <span className={styles.email}>{user.email}</span> on{' '}
        <span className={styles.host}>{organization.effectiveHost}</span>
      </div>
    </AccountContentContainer>
  );
};
